//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { School as SchoolIcon } from '@mui/icons-material';

import {
  CHAT_PANEL_ID, CHAT_TOUR_ANCHORS
} from '@/components/Chat/chatPanelId';
import { HEADER_TOUR_ANCHORS } from '@/components/Header/headerTourAnchors';
import { DEMO_DATA_TOOL_ID } from '@/components/Header/usePinnedTools';
import { anchor, idSelector } from '@/components/Help/tours/tourDom';
import { TourInfo, TourStep } from '@/components/Help/tourTypes';
import {
  panelElementId, panelLauncherId, toolLauncherId
} from '@/components/Panels/panelIds';
import { demoRowSummary } from '@/utils/DemoDatasets';

export const GETTING_STARTED_TOUR_ID = 'getting-started';

// The steps OnboardingTourStarter drives, since the tour cannot see the app
// state they depend on: whether the server has data, whether the chat panel
// is open, whether chat is usable at all.
export const DEMO_PILL_STEP_ID = 'demo-pill';
export const DEMO_MENU_STEP_ID = 'demo-menu';
export const DATA_WAIT_STEP_ID = 'data-wait';
export const ASK_AI_STEP_ID = 'ask-ai';

const gettingStartedSteps: TourStep[] = [
  {
    id: DEMO_PILL_STEP_ID,
    target: idSelector(toolLauncherId(DEMO_DATA_TOOL_ID)),
    title: 'Welcome to Mission Control',
    content: 'Mission Control needs a graph to work with. The Demo data ' +
             'menu loads a sample one; open it to see what is offered. ' +
             `Loading one puts about ${demoRowSummary()} rows on the ` +
             'connected server.',
    placement: 'bottom',
    blockTargetInteraction: false,
    spotlightPadding: 6,
    actionHint: 'Click Demo data to continue',
    beforeStep: (tourActions) => { tourActions.setIsNextDisabled(true); },
    advanceOnClick: true
  },
  {
    id: DEMO_MENU_STEP_ID,
    target: anchor(HEADER_TOUR_ANCHORS.demoDataMenu),
    title: 'Choose a Demo Dataset',
    content: `Pick a dataset to load — about ${demoRowSummary()} rows. ` +
             'A dataset the server already holds is grayed out.',
    placement: 'left',
    blockTargetInteraction: false,
    spotlightPadding: 6,
    actionHint: 'Click a dataset to continue',
    beforeStep: (tourActions) => { tourActions.setIsNextDisabled(true); },
    advanceOnClick: true,
    // A click on a grayed-out row or the menu's padding chooses nothing.
    clickWithin: '[role="menuitem"]:not([aria-disabled="true"])',
    // The menu is open only after the pill was clicked. Next from the pill,
    // and Back from the step after, pass this by when it is closed.
    skipIfAbsent: true
  },
  {
    id: DATA_WAIT_STEP_ID,
    target: anchor(HEADER_TOUR_ANCHORS.datasetSelector),
    title: 'Your Dataset',
    content: 'The dataset you are working on shows here, and it is the one ' +
             'Ask AI works on. It appears as soon ' +
             'as its schema exists, while the rows keep loading in the ' +
             'background, and the tour continues on its own when it does. ' +
             'If the load fails, Back returns to Demo data.',
    placement: 'bottom',
    actionHint: 'Waiting for the demo data to load'
  },
  {
    id: ASK_AI_STEP_ID,
    target: idSelector(panelLauncherId(CHAT_PANEL_ID)),
    title: 'Ask AI',
    content: 'Ask AI answers questions about your graph in plain language. ' +
             'Click it to open the assistant. If it is grayed out, no model ' +
             'is configured on this server, and the tour ends here.',
    placement: 'bottom',
    blockTargetInteraction: false,
    spotlightPadding: 6,
    actionHint: 'Click Ask AI to continue',
    beforeStep: (tourActions) => { tourActions.setIsNextDisabled(true); },
    advanceOnClick: true
  },
  {
    target: idSelector(panelElementId(CHAT_PANEL_ID)),
    title: 'The Ask AI Panel',
    content: 'The panel floats over the page and can be dragged and resized. ' +
             'Its title bar parks it in the header, and the conversation is ' +
             'still there when you bring it back.',
    // Over the panel rather than beside it: the panel is whatever size the
    // user last dragged it to, and one that fills the window leaves no side
    // with room, which put the tooltip off the top of the viewport.
    placement: 'center',
    spotlightPadding: 4,
    // Parking the panel, which this step invites, takes it and everything
    // inside it out of reach at once. Passed by rather than ending the tour.
    skipIfAbsent: true
  },
  {
    target: anchor(CHAT_TOUR_ANCHORS.message),
    title: 'Ask a Question',
    content: 'Type here and press Enter to send; Shift+Enter starts a new ' +
             'line. Or click one of the suggestions above to start with a ' +
             'question about your graph.',
    placement: 'top',
    skipIfAbsent: true
  },
  {
    target: anchor(CHAT_TOUR_ANCHORS.toolbar),
    title: 'Search and Clear',
    content: 'Search the conversation here, or clear it to start again. ' +
             'That is the tour: ask the assistant about your new graph.',
    placement: 'bottom',
    skipIfAbsent: true
  }
];

/**
 * The index of the step named `id`, derived so the steps can be reordered.
 * A name no step has is a programming error, and throws rather than handing
 * the starter an index of -1 to gate on.
 */
export const gettingStartedStepIndex = (id: string): number => {
  const index = gettingStartedSteps.findIndex((step) => step.id === id);
  if (index === -1)
    throw new Error(`Getting Started tour has no step named "${id}"`);
  return index;
};

// The steps the starter treats as the demo run, named rather than written
// down as a range, so a step inserted among them is not swallowed silently.
export const DEMO_STEP_INDEXES: readonly number[] = [
  DEMO_PILL_STEP_ID, DEMO_MENU_STEP_ID, DATA_WAIT_STEP_ID
].map(gettingStartedStepIndex);

export const gettingStartedTourInfo: TourInfo = {
  id: GETTING_STARTED_TOUR_ID,
  title: 'Getting Started',
  description: 'Load a demo dataset and ask the assistant about it',
  icon: <SchoolIcon />,
  autoStarts: true,
  steps: gettingStartedSteps
};
